import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartSpec } from 'shared';
import { barPath, labelStride, niceTicks, ringPath, sliceAngles, truncate } from './scale';

/**
 * A chart in a report, drawn from the data in its spec and nothing else.
 *
 * Every string reaches the page as a React text node, never as markup, so
 * whatever an agent wrote in a label cannot become HTML. Colour comes from six
 * fixed slots in `index.css`; a series keeps its slot, so the same series is
 * the same colour in every chart of the report.
 *
 * The drawing is hidden from assistive technology and the same numbers are
 * always in the page as a table, visible on request and read aloud otherwise.
 */

const WIDTH = 640;
const HEIGHT = 280;
const PAD = { top: 30, right: 16, bottom: 40, left: 52 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

const color = (index: number) => `var(--chart-${(index % 6) + 1})`;

interface Hover {
  index: number;
  /** Position as a share of the drawing, so the tooltip follows the scaling. */
  x: number;
  y: number;
}

export function ReportChart({ spec }: { spec: ChartSpec }) {
  const { t, i18n } = useTranslation();
  const [showTable, setShowTable] = useState(false);
  const [hover, setHover] = useState<Hover | null>(null);
  const titleId = useId();

  const format = useMemo(() => {
    const full = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 });
    const compact = new Intl.NumberFormat(i18n.language, {
      notation: 'compact',
      maximumFractionDigits: 1,
    });
    return { full: (v: number) => full.format(v), axis: (v: number) => compact.format(v) };
  }, [i18n.language]);

  const withUnit = (v: number) => (spec.unit ? `${format.full(v)} ${spec.unit}` : format.full(v));

  const isDonut = spec.type === 'donut';
  const showLegend = isDonut || spec.series.length > 1;

  return (
    <figure
      className="not-prose my-4 rounded-xl p-4 ring-1 ring-gray-200 dark:ring-gray-800"
      style={{ background: 'var(--chart-surface)', color: 'var(--chart-ink)' }}
      aria-labelledby={spec.title ? titleId : undefined}
      data-testid="report-chart"
      data-chart-type={spec.type}
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        {spec.title ? (
          <figcaption id={titleId} className="text-sm font-semibold">
            {spec.title}
          </figcaption>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          aria-pressed={showTable}
          className="shrink-0 rounded-md print:hidden px-2 py-1 text-xs font-medium underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          style={{ color: 'var(--chart-ink-2)' }}
        >
          {showTable ? t('chart.showChart') : t('chart.showTable')}
        </button>
      </div>

      {!showTable && (
        <div className="relative" onPointerLeave={() => setHover(null)}>
          {isDonut ? (
            <Donut spec={spec} hover={hover} setHover={setHover} format={format.full} />
          ) : (
            <Axes spec={spec} hover={hover} setHover={setHover} format={format.axis} />
          )}
          {hover && (
            <Tooltip spec={spec} hover={hover} formatValue={withUnit} donut={isDonut} />
          )}
        </div>
      )}

      {!showTable && showLegend && (
        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs" aria-hidden="true">
          {(isDonut ? spec.labels : spec.series.map((s) => s.name)).map((name, i) => (
            <li key={`${name}-${i}`} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: color(i) }}
              />
              <span style={{ color: 'var(--chart-ink-2)' }}>{name}</span>
              {isDonut && (
                <span className="tabular-nums">
                  {withUnit(spec.series[0].values[i])}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <table
        className={
          showTable
            ? 'mt-1 w-full text-left text-xs tabular-nums'
            : 'sr-only'
        }
      >
        <caption className="sr-only">{spec.title ?? t('chart.dataTable')}</caption>
        <thead>
          <tr style={{ color: 'var(--chart-ink-2)' }}>
            <th scope="col" className="py-1 pr-3 font-medium" />
            {spec.series.map((s) => (
              <th key={s.name} scope="col" className="py-1 pr-3 text-right font-medium">
                {s.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {spec.labels.map((label, i) => (
            <tr key={`${label}-${i}`} className="border-t" style={{ borderColor: 'var(--chart-grid)' }}>
              <th scope="row" className="py-1 pr-3 font-normal">
                {label}
              </th>
              {spec.series.map((s) => (
                <td key={s.name} className="py-1 pr-3 text-right">
                  {withUnit(s.values[i])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

interface DrawingProps {
  spec: ChartSpec;
  hover: Hover | null;
  setHover: (hover: Hover | null) => void;
  format: (value: number) => string;
}

/** Bars and lines share their frame: the same axes, grid and hit bands. */
function Axes({ spec, hover, setHover, format }: DrawingProps) {
  const all = spec.series.flatMap((s) => s.values);
  const isBar = spec.type === 'bar';

  // Bars are read by length, so their axis starts at zero. A line is read by
  // shape and may zoom in on where the values are.
  const lo = isBar ? Math.min(0, ...all) : Math.min(...all);
  const hi = isBar ? Math.max(0, ...all) : Math.max(...all);
  const ticks = niceTicks(lo, hi);
  const min = ticks[0];
  const max = ticks[ticks.length - 1];

  const y = (v: number) => PAD.top + PLOT_H - ((v - min) / (max - min)) * PLOT_H;
  const n = spec.labels.length;
  const band = PLOT_W / n;
  const center = (i: number) => PAD.left + band * (i + 0.5);
  const stride = labelStride(n, 8);
  const baseline = y(Math.max(min, Math.min(0, max)));

  const groupWidth = Math.min(band * 0.7, 56 * spec.series.length);
  const barWidth = Math.max(4, groupWidth / spec.series.length - 2);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="h-auto w-full"
      aria-hidden="true"
      focusable="false"
    >
      {ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={PAD.left}
            x2={WIDTH - PAD.right}
            y1={y(tick)}
            y2={y(tick)}
            stroke="var(--chart-grid)"
            strokeWidth={tick === 0 ? 1.5 : 1}
          />
          <text
            x={PAD.left - 8}
            y={y(tick)}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize="11"
            fill="var(--chart-ink-2)"
          >
            {format(tick)}
          </text>
        </g>
      ))}

      {spec.unit && (
        <text x={PAD.left - 8} y={12} textAnchor="end" fontSize="11" fill="var(--chart-ink-2)">
          {spec.unit}
        </text>
      )}

      {spec.labels.map((label, i) =>
        i % stride === 0 ? (
          <text
            key={`${label}-${i}`}
            x={center(i)}
            y={HEIGHT - PAD.bottom + 18}
            textAnchor="middle"
            fontSize="11"
            fill="var(--chart-ink-2)"
          >
            {truncate(label, Math.max(6, Math.floor((band * stride) / 6.5)))}
          </text>
        ) : null
      )}

      {hover && !isBar && (
        <line
          x1={center(hover.index)}
          x2={center(hover.index)}
          y1={PAD.top}
          y2={PAD.top + PLOT_H}
          stroke="var(--chart-ink-2)"
          strokeWidth={1}
          strokeDasharray="3 3"
        />
      )}

      {isBar &&
        spec.series.map((s, si) =>
          s.values.map((v, i) => {
            const x0 = center(i) - groupWidth / 2 + si * (groupWidth / spec.series.length) + 1;
            return (
              <path
                key={`${s.name}-${i}`}
                d={barPath(x0, baseline, y(v), barWidth)}
                fill={color(si)}
                opacity={hover && hover.index !== i ? 0.45 : 1}
              />
            );
          })
        )}

      {!isBar &&
        spec.series.map((s, si) => (
          <g key={s.name}>
            <polyline
              points={s.values.map((v, i) => `${center(i)},${y(v)}`).join(' ')}
              fill="none"
              stroke={color(si)}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {(n <= 12 || hover) &&
              s.values.map((v, i) =>
                n <= 12 || hover?.index === i ? (
                  <circle
                    key={i}
                    cx={center(i)}
                    cy={y(v)}
                    r={4}
                    fill={color(si)}
                    stroke="var(--chart-surface)"
                    strokeWidth={2}
                  />
                ) : null
              )}
          </g>
        ))}

      {/* One band per label, taller than any mark, so a thin bar is easy to hit. */}
      {spec.labels.map((label, i) => (
        <rect
          key={`hit-${label}-${i}`}
          x={PAD.left + band * i}
          y={PAD.top}
          width={band}
          height={PLOT_H}
          fill="transparent"
          onPointerMove={() =>
            setHover({ index: i, x: center(i) / WIDTH, y: PAD.top / HEIGHT })
          }
        />
      ))}
    </svg>
  );
}

function Donut({ spec, hover, setHover, format }: DrawingProps) {
  const { t } = useTranslation();
  const values = spec.series[0].values;
  const size = 220;
  const c = size / 2;
  const slices = useMemo(() => sliceAngles(values), [values]);
  const total = values.reduce((sum, v) => sum + v, 0);

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="mx-auto block h-auto w-full max-w-[240px]"
      aria-hidden="true"
      focusable="false"
    >
      {slices.map((slice) => (
        <path
          key={slice.index}
          d={ringPath(c, c, 100, 62, slice.start, slice.end)}
          fill={color(slice.index)}
          stroke="var(--chart-surface)"
          strokeWidth={2}
          opacity={hover && hover.index !== slice.index ? 0.45 : 1}
          onPointerMove={() => {
            const mid = (slice.start + slice.end) / 2;
            setHover({
              index: slice.index,
              x: (c + 81 * Math.sin(mid)) / size,
              y: (c - 81 * Math.cos(mid)) / size,
            });
          }}
        />
      ))}
      <text
        x={c}
        y={c - 4}
        textAnchor="middle"
        fontSize="20"
        fontWeight="600"
        fill="var(--chart-ink)"
      >
        {format(total)}
      </text>
      <text x={c} y={c + 14} textAnchor="middle" fontSize="11" fill="var(--chart-ink-2)">
        {t('chart.total')}
      </text>
    </svg>
  );
}

function Tooltip({
  spec,
  hover,
  formatValue,
  donut,
}: {
  spec: ChartSpec;
  hover: Hover;
  formatValue: (value: number) => string;
  donut: boolean;
}) {
  const flip = hover.x > 0.6;
  const total = donut ? spec.series[0].values.reduce((sum, v) => sum + v, 0) : 0;

  return (
    <div
      className="pointer-events-none absolute z-10 min-w-[8rem] rounded-lg px-3 py-2 text-xs shadow-lg ring-1 ring-gray-200 dark:ring-gray-700"
      style={{
        left: `${hover.x * 100}%`,
        top: `${hover.y * 100}%`,
        transform: `translate(${flip ? 'calc(-100% - 12px)' : '12px'}, 0)`,
        background: 'var(--chart-surface)',
        color: 'var(--chart-ink)',
      }}
      role="presentation"
      data-testid="chart-tooltip"
    >
      <div className="mb-1 font-semibold">{spec.labels[hover.index]}</div>
      {spec.series.map((s, si) => (
        <div key={s.name} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5" style={{ color: 'var(--chart-ink-2)' }}>
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: color(donut ? hover.index : si) }}
            />
            {donut ? '' : s.name}
          </span>
          <span className="tabular-nums">
            {formatValue(s.values[hover.index])}
            {donut && total > 0 && (
              <span style={{ color: 'var(--chart-ink-2)' }}>
                {' '}
                {Math.round((s.values[hover.index] / total) * 100)}%
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
